import { useInfiniteQuery } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import {
  UserHistoryItemKind,
  UserHistoryPageDto,
} from '../../../models/payment/dto/user-history.dto';

export const GET_USER_HISTORY_QUERY_KEY = 'admin-user-history';

export type UseGetUserHistoryParams = {
  userId: string | undefined;
  kinds?: UserHistoryItemKind[];
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
};

/**
 * Admin unified history feed (`GET /payments/admin/users/:userId/history`,
 * epic Phase A T3@ceo-review). Cursor-paginated — TanStack Query's
 * `useInfiniteQuery` is the natural fit over the offset/`page` pattern the
 * rest of this codebase's admin tables use (`useGetAdminTransactions`,
 * `useGetAccessLogs`): the API has no `page` param, only a keyset
 * `(occurredAt, id)` cursor (epic point 6 — stable across a 4/5-source
 * union where a same-ms tie needs the id tiebreak).
 *
 * `method` filtering is NOT sent server-side: `parseUserHistoryQuery`
 * (api/src/payment/service/parse-user-history-query.ts) never reads a
 * `method` query param even though `UserHistoryFiltersDto.method` exists in
 * the DTO, and `get-user-history.ts` only logs it (never filters on it) —
 * confirmed by reading both files, not assumed. `UserHistorySection` filters
 * by method CLIENT-SIDE on whatever pages are already loaded instead.
 */
export const useGetUserHistory = ({
  userId,
  kinds,
  dateFrom,
  dateTo,
  limit = 50,
}: UseGetUserHistoryParams) => {
  const client = useApiClient();
  // Stable string, not the array reference, in the query key — `kinds` is
  // rebuilt on every render in the caller (a new array literal each time),
  // which would otherwise refetch on every keystroke elsewhere on the page.
  const kindsKey = kinds && kinds.length > 0 ? kinds.slice().sort().join(',') : '';

  return useInfiniteQuery({
    queryKey: [GET_USER_HISTORY_QUERY_KEY, userId, kindsKey, dateFrom, dateTo, limit],
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      const params = new URLSearchParams();
      if (kinds && kinds.length > 0) params.append('kinds', kinds.join(','));
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      if (pageParam) params.append('cursor', pageParam);
      params.append('limit', String(limit));

      return client.fetchApi<UserHistoryPageDto, {}>({
        endpoint: `payments/admin/users/${userId}/history?${params.toString()}`,
        method: 'GET',
      });
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: Boolean(userId),
    staleTime: 30 * 1000,
  });
};
